import { useEffect, useLayoutEffect, useRef, useState } from "react";

interface Tip {
  text: string;
  rect: DOMRect;
}

// One tooltip for every element with a data-tip attribute. It is fixed to
// the viewport, outside any scrolling panel, so showing it can never add
// scroll overflow or reflow the content under the pointer.
export default function TooltipHost() {
  const [tip, setTip] = useState<Tip | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const show = (e: Event) => {
      const el = (e.target as Element | null)?.closest?.("[data-tip]");
      setTip(el ? { text: el.getAttribute("data-tip")!, rect: el.getBoundingClientRect() } : null);
    };
    const hide = () => setTip(null);
    document.addEventListener("pointerover", show);
    document.addEventListener("focusin", show);
    document.addEventListener("pointerdown", hide);
    document.addEventListener("scroll", hide, true);
    document.addEventListener("keydown", hide);
    return () => {
      document.removeEventListener("pointerover", show);
      document.removeEventListener("focusin", show);
      document.removeEventListener("pointerdown", hide);
      document.removeEventListener("scroll", hide, true);
      document.removeEventListener("keydown", hide);
    };
  }, []);

  // Above the element, centered, nudged to stay inside the window; below it
  // when there is no room above.
  useLayoutEffect(() => {
    if (!tip || !box.current) {
      setPos(null);
      return;
    }
    const { width, height } = box.current.getBoundingClientRect();
    const margin = 6;
    const left = Math.min(Math.max(margin, tip.rect.left + tip.rect.width / 2 - width / 2), window.innerWidth - width - margin);
    const above = tip.rect.top - height - margin;
    setPos({ left, top: above >= margin ? above : tip.rect.bottom + margin });
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={box}
      className="tooltip"
      role="tooltip"
      style={{ left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? "visible" : "hidden" }}
    >
      {tip.text}
    </div>
  );
}
